import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { AlarmModelTimeWindow } from "app/models/alarm.model";
import { DataTypeModelKey } from "app/models/dataType.model";
import { DeviceAggregatedData, DeviceDataModel, DeviceDataType } from "app/models/deviceData.model";
import { EventModelSeverity } from "app/models/event.model";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { DataService } from "app/services/api/data.service";
import { ProjectDeviceService } from "app/services/api/projectDevice.service";
import * as moment from "moment";
import { IMqttMessage } from "ngx-mqtt";
import { ToastrService } from "ngx-toastr";
import { BehaviorSubject, Subscription } from "rxjs";
import { DeviceMqttService } from "../../services/api/device.mqtt.service";
import { locale as catalan } from "../i18n/ca";
import { locale as english } from "../i18n/en";
import { locale as spanish } from "../i18n/es";
import * as Highcharts from "highcharts/highcharts";
import { chartCellularLevel5MinutesCard } from "app/chart-config/chart-cellular-level-5-minutes-card";

@Component({
    selector: "device-card-cellular",
    templateUrl: "./device-card-cellular.component.html",
    styleUrls: ["./device-card-cellular.component.scss"],
    standalone: false,
})
export class DeviceCardCellular implements OnInit, OnChanges, OnDestroy {
    @Input() device: ProjectDeviceModel;
    @Input() showDeviceLink = true;
    @Input() editGraphViews = false;

    readonly device$ = new BehaviorSubject<ProjectDeviceModel | null>(null);

    dataType = DataTypeModelKey.cellullarSignalLevel;
    loadingData = false;
    deviceData: DeviceDataModel[];
    sensorGraphData: any = null;

    statusValue: EventModelSeverity;
    statusAvg5: EventModelSeverity;

    Highcharts = Highcharts; // required
    chart = JSON.parse(JSON.stringify(chartCellularLevel5MinutesCard));
    chartRef: Highcharts.Chart | null = null;

    subsInstant: Subscription;
    subsStatus: Subscription;
    subsAvg5: Subscription;
    subsBucket: Subscription;

    constructor(
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private readonly dataService: DataService,
        private readonly projectDeviceService: ProjectDeviceService,
        private toastr: ToastrService,
        private deviceService: DeviceMqttService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        this._fuseTranslationLoaderService.loadTranslations(english, spanish, catalan);
    }

    ngOnInit() {
        if (!this.device?.id) {
            return;
        }
        this.fetchData();
        this.subscribeToTopics();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["device"]) {
            this.device$.next(this.device);
        }
    }

    ngOnDestroy(): void {
        this.subsInstant.unsubscribe();
        this.subsStatus.unsubscribe();
        this.subsAvg5.unsubscribe();
        this.subsBucket.unsubscribe();
    }

    chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
        this.chartRef = chart;
        this.applyChartData();
    };

    subscribeToTopics(): void {
        this.subsInstant = this.deviceService
            .deviceTopic(this.device.id, DeviceDataType.instant, DataTypeModelKey.cellullarSignalLevel)
            .subscribe((data: IMqttMessage) => {
                const item = JSON.parse(data.payload.toString());
                this.device.lastData.cellullarSignalLevel.value = item.value;
                this.device.lastData.cellullarSignalLevel.timestamp = item.timestamp;
                this.cdr.detectChanges();
            });

        this.subsStatus = this.deviceService.deviceTopic(this.device.id, DeviceDataType.status, null).subscribe((data: IMqttMessage) => {
            const item = JSON.parse(data.payload.toString());
            this.device.connected = item.connected;
            this.device.activeEvents = item.activeEvents;
            this.checkAlarms();
            this.cdr.detectChanges();
        });

        this.subsAvg5 = this.deviceService
            .deviceTopic(this.device.id, DeviceDataType.fiveMinutes, DataTypeModelKey.cellullarSignalLevel)
            .subscribe((data: IMqttMessage) => {
                const item = JSON.parse(data.payload.toString());
                this.device.dataAggregation.cellullarSignalLevel.average = item.average;
                this.cdr.detectChanges();
            });

        this.subsBucket = this.deviceService
            .deviceTopic(this.device.id, DeviceDataType.bucket, DataTypeModelKey.cellullarSignalLevel)
            .subscribe((data: IMqttMessage) => {
                const item = JSON.parse(data.payload.toString());

                const last = this.deviceData[this.deviceData.length - 1];

                if (moment(last.date).format("hh:mm") == moment(item.date).format("hh:mm")) {
                    this.deviceData[this.deviceData.length - 1].aggregation = item.aggregation;
                } else {
                    this.deviceData.shift();
                    this.deviceData.push(item);
                }
                this.createSensorData();
                this.cdr.detectChanges();
            });
    }

    private refreshData() {
        if (this.loadingData) {
            return;
        }
        this.loadingData = true;

        this.fetchDevice();
        this.fetchData();
    }

    private checkAlarms() {
        this.statusAvg5 = EventModelSeverity.EventModelSeveritySuccess;
        this.statusValue = this.device.getAlarmStatus(
            DataTypeModelKey.cellullarSignalLevel,
            AlarmModelTimeWindow.AlarmModelTimeWindowInstant,
        );
    }

    private fetchDevice() {
        // Fetch bucket of data of last hour (12 buckets)
        this.projectDeviceService.get(this.device.id).subscribe(
            (data: ProjectDeviceModel) => {
                this.device = Object.assign(new ProjectDeviceModel(), data);
                this.device$.next(this.device);
                this.checkAlarms();
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    private fetchData() {
        // Fetch bucket of data of last hour (12 buckets)
        this.dataService.getDeviceDataSample(this.device.id, [DataTypeModelKey.cellullarSignalLevel], 12, true).subscribe(
            (data: DeviceAggregatedData) => {
                this.deviceData = data[DataTypeModelKey.cellullarSignalLevel];
                // From API data are coming from the most recent to the latest, reverting the order.
                this.deviceData.sort((d1, d2) => new Date(d1.date).getTime() - new Date(d2.date).getTime());
                this.createSensorData();
                this.loadingData = false;
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                this.loadingData = false;
            },
        );
    }

    private createSensorData() {
        if (!this.deviceData) {
            return;
        }

        const seriesData = [];

        this.deviceData.forEach((data) => {
            const value = Math.round(data.aggregation.average * 10) / 10;
            const date = moment(data.date).format("HH:mm");
            seriesData.push([date, value]);
        });

        this.chart.options.series[0].data = seriesData;
        this.applyChartData();
        this.cdr.detectChanges();
    }

    private applyChartData() {
        if (this.chartRef?.series?.[0]) {
            this.chartRef.series[0].setData(this.chart.options.series[0].data as any[], false);
            this.chartRef.redraw();
            requestAnimationFrame(() => this.chartRef?.reflow());
            return;
        }

        this.chart.update = true;
        this.chart.visible = true;
    }
}
